import {
	ADMIN_OR_ABOVE,
	NON_GUEST,
	SUPER_ADMIN_ONLY as SHARED_SUPER_ADMIN_ONLY,
	STAFF_OR_ABOVE,
	UserRole as UserRoleEnum,
} from "@excel-center/shared";
import {
	Baby,
	Briefcase,
	Building2,
	Calendar,
	CalendarClock,
	ChartBar,
	FileBarChart,
	Inbox,
	LayoutDashboard,
	type LucideIcon,
	Megaphone,
	Newspaper,
	Send,
	Settings,
	ShieldCheck,
	User,
	UserCog,
	Users,
	UsersRound,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AiAssistantSidebarItem } from "@/src/ai-assistant/components/ai-assistant-sidebar-item";
import { ANALYTICS_ROUTES } from "@/src/analytics/constants/analytics-routes.constants";
import { AUDIT_ROUTES } from "@/src/audit/constants/audit-routes.constants";
import type { UserRole } from "@/src/auth/types/auth.types";
import { CHILDCARE_ROUTES } from "@/src/childcare/constants/childcare-routes.constants";
import { useChildcareAccess } from "@/src/childcare/hooks/use-childcare-access";
import { SidebarCollapsibleItem, SidebarItem, SidebarSection } from "@/src/common/components/sidebar";
import { DASHBOARD_ROUTES } from "@/src/dashboard/constants/dashboard-routes.constants";
import { GROUPS_ROUTES } from "@/src/groups/constants/groups-routes.constants";
import { JOBS_ROUTES } from "@/src/jobs/constants/jobs-routes.constants";
import { MESSAGING_ROUTES } from "@/src/messaging/constants/messaging-routes.constants";
import { useUnreadMessagesCount } from "@/src/messaging/hooks/use-unread-messages-count";
import { POST_ROUTES } from "@/src/post/constants/post-routes.constants";
import { PROFILE_ROUTES } from "@/src/profile/constants/profile-routes.constants";
import { SCHOOLS_ROUTES } from "@/src/schools/constants/schools-routes.constants";
import { SETTINGS_ROUTES } from "@/src/settings/settings-routes.constants";
import { STAFFS_ROUTES } from "@/src/staffs/constants/staffs-routes.constants";
import { STUDENT_CENTRAL_ROUTES } from "@/src/student-central/constants/student-central-routes.constants";
import { STUDENTS_ROUTES } from "@/src/students/constants/students-routes.constants";

type NavLabelKey =
	| "nav.feed"
	| "nav.student_central"
	| "nav.jobs"
	| "nav.dashboard"
	| "nav.students"
	| "nav.staff"
	| "nav.job_management"
	| "nav.groups"
	| "nav.schools"
	| "nav.messaging"
	| "nav.analytics"
	| "nav.audit_log"
	| "nav.settings"
	| "nav.my_profile"
	| "nav.my_schedule"
	| "nav.my_childcare"
	| "nav.childcare"
	| "nav.childcare_attendance"
	| "nav.childcare_children"
	| "nav.childcare_reports"
	| "nav.childcare_terms";

type NavTitleKey = "nav.overview" | "nav.my_account" | "nav.management" | "nav.admin_tools" | "nav.account";

interface NavSubItem {
	icon: LucideIcon;
	labelKey: NavLabelKey;
	to: string;
	end?: boolean;
}

interface NavItem {
	icon: LucideIcon;
	labelKey: NavLabelKey;
	to: string;
	roles: readonly UserRole[];
	end?: boolean;
	/** Hidden unless the user has childcare access (membership / admin). */
	requiresChildcareAccess?: boolean;
	/** When set, the item renders as an expandable group with this sub-nav. */
	subItems?: readonly NavSubItem[];
	/** Route prefix used to detect whether the expandable group is active. */
	basePath?: string;
}

interface NavSection {
	id: string;
	titleKey: NavTitleKey;
	items: readonly NavItem[];
}

const STUDENT_AND_UP: readonly UserRole[] = NON_GUEST;
const STAFF_AND_UP: readonly UserRole[] = STAFF_OR_ABOVE;
const ADMIN_AND_UP: readonly UserRole[] = ADMIN_OR_ABOVE;
const SUPER_ADMIN_ONLY: readonly UserRole[] = SHARED_SUPER_ADMIN_ONLY;
const STUDENT_ONLY: readonly UserRole[] = [UserRoleEnum.STUDENT];

/**
 * Role → sidebar mapping per PRD §5. Each item lists the roles that see
 * it; sections disappear when every item is filtered out.
 *
 * `staff` is General Staff: Overview + read-only Students + Messaging +
 * AI only. Management surfaces start at `admin`; Analytics, Audit, Staff,
 * Schools, Imports and Terms are super-admin-only.
 */
const SECTIONS: readonly NavSection[] = [
	{
		id: "overview",
		titleKey: "nav.overview",
		items: [
			{ icon: Newspaper, labelKey: "nav.feed", to: POST_ROUTES.appFeed, roles: STUDENT_AND_UP },
			{ icon: Megaphone, labelKey: "nav.student_central", to: STUDENT_CENTRAL_ROUTES.appStudentCentral, roles: STUDENT_AND_UP },
			{ icon: Briefcase, labelKey: "nav.jobs", to: JOBS_ROUTES.appJobs, roles: STUDENT_AND_UP, end: true },
		],
	},
	{
		id: "my_account",
		titleKey: "nav.my_account",
		items: [
			{ icon: User, labelKey: "nav.my_profile", to: PROFILE_ROUTES.myProfile, roles: STUDENT_ONLY },
			{ icon: Calendar, labelKey: "nav.my_schedule", to: PROFILE_ROUTES.mySchedule, roles: STUDENT_ONLY },
			{ icon: Baby, labelKey: "nav.my_childcare", to: CHILDCARE_ROUTES.myChildren, roles: STUDENT_ONLY, end: true },
			{ icon: Inbox, labelKey: "nav.messaging", to: MESSAGING_ROUTES.messaging, roles: STUDENT_ONLY },
		],
	},
	{
		id: "management",
		titleKey: "nav.management",
		items: [
			{ icon: LayoutDashboard, labelKey: "nav.dashboard", to: DASHBOARD_ROUTES.dashboard, roles: ADMIN_AND_UP },
			{ icon: Users, labelKey: "nav.students", to: STUDENTS_ROUTES.students, roles: STAFF_AND_UP },
			{ icon: UserCog, labelKey: "nav.staff", to: STAFFS_ROUTES.staffs, roles: ADMIN_AND_UP },
			{ icon: Briefcase, labelKey: "nav.job_management", to: JOBS_ROUTES.appJobsManage, roles: ADMIN_AND_UP },
			{ icon: UsersRound, labelKey: "nav.groups", to: GROUPS_ROUTES.groups, roles: ADMIN_AND_UP },
			{ icon: Building2, labelKey: "nav.schools", to: SCHOOLS_ROUTES.schools, roles: SUPER_ADMIN_ONLY },
			{
				icon: Baby,
				labelKey: "nav.childcare",
				to: CHILDCARE_ROUTES.attendance,
				basePath: CHILDCARE_ROUTES.attendance,
				roles: STAFF_AND_UP,
				requiresChildcareAccess: true,
				subItems: [
					{ icon: CalendarClock, labelKey: "nav.childcare_attendance", to: CHILDCARE_ROUTES.attendance, end: true },
					{ icon: Baby, labelKey: "nav.childcare_children", to: CHILDCARE_ROUTES.children },
					{ icon: FileBarChart, labelKey: "nav.childcare_reports", to: CHILDCARE_ROUTES.reports },
				],
			},
			{ icon: CalendarClock, labelKey: "nav.childcare_terms", to: CHILDCARE_ROUTES.terms, roles: SUPER_ADMIN_ONLY },
		],
	},
	{
		id: "admin_tools",
		titleKey: "nav.admin_tools",
		items: [
			{ icon: Send, labelKey: "nav.messaging", to: MESSAGING_ROUTES.messaging, roles: STAFF_AND_UP },
			{ icon: ChartBar, labelKey: "nav.analytics", to: ANALYTICS_ROUTES.analytics, roles: SUPER_ADMIN_ONLY },
			{ icon: ShieldCheck, labelKey: "nav.audit_log", to: AUDIT_ROUTES.audit, roles: SUPER_ADMIN_ONLY },
		],
	},
	{
		id: "account",
		titleKey: "nav.account",
		items: [{ icon: Settings, labelKey: "nav.settings", to: SETTINGS_ROUTES.settings, roles: STAFF_AND_UP }],
	},
];

interface RoleSidebarProps {
	role: UserRole | null;
}

/**
 * Renders the full sidebar tree filtered by `role`. Nothing renders
 * until a role is known (guests hit `<ProtectedRoute />` and bounce
 * away before this can render in practice).
 */
export function RoleSidebar({ role }: RoleSidebarProps): ReactNode {
	const { t } = useTranslation("common");
	const { count: unreadCount } = useUnreadMessagesCount();
	const { hasAccess: hasChildcareAccess } = useChildcareAccess();
	if (!role) return null;

	const unreadLabel = unreadCount > 99 ? "99+" : String(unreadCount);

	const showAiAssistant = STAFF_AND_UP.includes(role);

	return (
		<>
			{showAiAssistant ? <AiAssistantSidebarItem /> : null}
			{SECTIONS.map((section) => {
				const visibleItems = section.items.filter(
					(item) => item.roles.includes(role) && (!item.requiresChildcareAccess || hasChildcareAccess),
				);
				if (visibleItems.length === 0) return null;
				return (
					<SidebarSection key={section.id} title={t(section.titleKey)}>
						{visibleItems.map((item) => {
							if (item.subItems) {
								return (
									<SidebarCollapsibleItem
										key={`${section.id}:${item.to}`}
										icon={item.icon}
										label={t(item.labelKey)}
										to={item.to}
										items={item.subItems.map((sub) => ({
											icon: sub.icon,
											label: t(sub.labelKey),
											to: sub.to,
											end: sub.end,
										}))}
									/>
								);
							}
							const isMessaging = item.to === MESSAGING_ROUTES.messaging;
							const badge = isMessaging && unreadCount > 0 ? unreadLabel : undefined;
							return (
								<SidebarItem
									key={`${section.id}:${item.to}`}
									icon={item.icon}
									label={t(item.labelKey)}
									to={item.to}
									end={item.end}
									badge={badge}
								/>
							);
						})}
					</SidebarSection>
				);
			})}
		</>
	);
}
